/**
 * Scrolling Ticker Component
 *
 * JS-driven infinite marquee with drag/swipe support.
 * Replaces the previous CSS keyframe animation so that auto-scroll
 * and user-initiated drag share one position state.
 *
 * - Auto-scrolls continuously via requestAnimationFrame
 * - Pauses on hover (desktop) and during drag
 * - Drag works on both mouse and touch
 * - Friction-based momentum on release, then auto-scroll resumes
 *
 * @package oska
 */

class ScrollingTicker {
  constructor(el) {
    this.ticker = el;
    this.track = el.querySelector(".ticker__track");
    this.groups = el.querySelectorAll(".ticker__group");

    if (!this.track || this.groups.length < 2) return;

    // Position state (px, moves negative for left scroll)
    this.position = 0;

    // Half-width = width of one group (the wrap point)
    this.halfWidth = 0;

    // Auto-scroll speed (px per frame at 60 fps)
    // Current CSS was 30s for 50% → we'll measure and match
    this.speed = 0;
    this.baseSpeed = 0; // stored after measurement

    // Interaction state
    this.isDragging = false;
    this.hasDragged = false;
    this.isHovering = false;
    this.shouldSuppressClick = false;

    // Drag tracking
    this.dragStartX = 0;
    this.dragStartPosition = 0;
    this.dragVelocity = 0;
    this.lastDragX = 0;
    this.lastDragTime = 0;

    // Momentum state
    this.momentumVelocity = 0;
    this.isMomentum = false;

    // Friction config
    this.friction = 0.95;
    this.minVelocity = 0.3;

    // rAF id
    this.rafId = null;

    this.init();
  }

  init() {
    this.measure();
    this.bindEvents();
    this.startLoop();
  }

  /**
   * Measure group width and calculate auto-scroll speed.
   */
  measure() {
    const group = this.groups[0];
    // Include the gap (pr-4 = 16px) by measuring with margin
    const style = getComputedStyle(group);
    this.halfWidth =
      group.offsetWidth +
      parseFloat(style.marginRight || 0) +
      parseFloat(style.paddingRight || 0);

    // If the group has a gap applied via flex gap on the parent track level,
    // we might need to account for it. Measure the actual track scroll extent:
    // halfWidth = total track width / 2 since we have exactly 2 identical groups.
    this.halfWidth = this.track.scrollWidth / 2;

    // Match the old 30-second CSS animation speed:
    // distance per frame = halfWidth / (30s * 60fps)
    this.baseSpeed = this.halfWidth / (30 * 60);
    this.speed = this.baseSpeed;
  }

  /**
   * Bind mouse, touch, hover, and resize events.
   */
  bindEvents() {
    // Hover (desktop) — pause auto-scroll
    this.ticker.addEventListener("mouseenter", () => {
      this.isHovering = true;
    });
    this.ticker.addEventListener("mouseleave", () => {
      this.isHovering = false;
    });

    // Mouse drag
    this.track.addEventListener("mousedown", (e) => this.handleDragStart(e));
    document.addEventListener("mousemove", (e) => this.handleDragMove(e));
    document.addEventListener("mouseup", (e) => this.handleDragEnd(e));

    // Touch drag
    this.track.addEventListener("touchstart", (e) => this.handleDragStart(e), {
      passive: true,
    });
    this.track.addEventListener("touchmove", (e) => this.handleDragMove(e), {
      passive: false,
    });
    this.track.addEventListener("touchend", (e) => this.handleDragEnd(e));

    // Prevent native link dragging from hijacking ticker drag interactions.
    this.track.addEventListener("dragstart", (e) => e.preventDefault());

    this.track.addEventListener("click", (e) => {
      if (!this.shouldSuppressClick) return;

      e.preventDefault();
      e.stopPropagation();
      this.shouldSuppressClick = false;
    });

    // Resize — re-measure
    window.addEventListener("resize", () => {
      this.measure();
    });
  }

  // -------------------------------------------------------------------------
  // Drag handlers
  // -------------------------------------------------------------------------

  handleDragStart(e) {
    const clientX = e.type === "touchstart" ? e.touches[0].clientX : e.clientX;

    // Stop any ongoing momentum
    this.isMomentum = false;
    this.momentumVelocity = 0;
    this.shouldSuppressClick = false;

    this.isDragging = true;
    this.hasDragged = false;
    this.dragStartX = clientX;
    this.dragStartPosition = this.position;
    this.lastDragX = clientX;
    this.lastDragTime = performance.now();
    this.dragVelocity = 0;

    this.ticker.classList.add("is-dragging");
  }

  handleDragMove(e) {
    if (!this.isDragging) return;

    const clientX = e.type === "touchmove" ? e.touches[0].clientX : e.clientX;
    const now = performance.now();

    const dx = clientX - this.dragStartX;

    // Detect real drag (past threshold)
    if (!this.hasDragged && Math.abs(dx) > 5) {
      this.hasDragged = true;
    }

    // Track velocity (exponential moving average)
    const timeDelta = now - this.lastDragTime;
    if (timeDelta > 0) {
      const instantV = (this.lastDragX - clientX) / timeDelta; // px/ms, positive = left
      this.dragVelocity = this.dragVelocity * 0.7 + instantV * 0.3;
    }
    this.lastDragX = clientX;
    this.lastDragTime = now;

    if (this.hasDragged) {
      // Move position: dragging right → position increases (scroll right = content moves right)
      this.position = this.dragStartPosition + dx;
      this.wrapPosition();

      // Prevent vertical scroll while dragging horizontally on touch
      if (e.type === "touchmove") {
        e.preventDefault();
      }
    }
  }

  handleDragEnd() {
    if (!this.isDragging) return;

    this.isDragging = false;
    this.ticker.classList.remove("is-dragging");

    if (!this.hasDragged) return;

    this.shouldSuppressClick = true;

    // Apply momentum if velocity is meaningful
    if (Math.abs(this.dragVelocity) > 0.05) {
      // Convert from px/ms to px/frame (at 60fps ≈ 16.67ms/frame)
      this.momentumVelocity = this.dragVelocity * 16.67;
      this.isMomentum = true;
    }
  }

  // -------------------------------------------------------------------------
  // Position wrapping for seamless loop
  // -------------------------------------------------------------------------

  wrapPosition() {
    if (this.halfWidth <= 0) return;
    // Keep position in the range (-halfWidth, 0]
    // When scrolling left (position goes negative), wrap at -halfWidth back to 0
    // When dragging right (position goes positive), wrap at 0 back to -halfWidth
    while (this.position <= -this.halfWidth) {
      this.position += this.halfWidth;
    }
    while (this.position > 0) {
      this.position -= this.halfWidth;
    }
  }

  // -------------------------------------------------------------------------
  // Animation loop
  // -------------------------------------------------------------------------

  startLoop() {
    const tick = () => {
      if (this.isDragging) {
        // Position is set directly by drag handler; just apply transform
      } else if (this.isMomentum) {
        // Apply momentum with friction
        this.position -= this.momentumVelocity;
        this.momentumVelocity *= this.friction;

        if (Math.abs(this.momentumVelocity) < this.minVelocity) {
          this.isMomentum = false;
          this.momentumVelocity = 0;
        }
      } else if (!this.isHovering) {
        // Auto-scroll
        this.position -= this.speed;
      }

      this.wrapPosition();
      this.track.style.transform = `translateX(${this.position}px)`;

      this.rafId = requestAnimationFrame(tick);
    };

    this.rafId = requestAnimationFrame(tick);
  }
}

/**
 * Initialize all ticker instances on the page.
 */
export default function initScrollingTicker() {
  document.querySelectorAll(".ticker").forEach((el) => {
    new ScrollingTicker(el);
  });
}

export { ScrollingTicker };
